'use client';
import classNames from 'classnames';
import React, { useEffect, useId, useRef, useState } from 'react';
import { ELIGIBILITY_TRANSLATIONS } from '../../components/Eligibility/i18n';
import { parseMeasureNumber } from '../../utils/attributeMeasure';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { currencyFormatterNoDecimal } from '../../utils/number';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { useProductEligibilityState } from './ProductEligibilityProvider';
import { SortByDropdown } from './SortByDropdown';

/** Long enough to be done typing a salary, short enough to feel live (R6). */
const COMMIT_DELAY_MS = 600;

/**
 * Grouped as the reader types ("2000" reads "2,000"), with the formatter the
 * calculator's Loan Amount field uses, so the two fields side by side match.
 */
const formatIncome = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  return digits ? currencyFormatterNoDecimal.format(Number(digits)) : '';
};

export type YourDetailsGroupProps = {
  /**
   * The reader finished entering a monthly income. Debounced, so it reports a
   * figure rather than every keystroke (Analytics 2).
   */
  onIncomeCommit?: (value: number | undefined) => void;
  /** A citizenship was chosen. Receives the option as the reader saw it. */
  onCitizenshipChange?: (value: string) => void;
};

/**
 * Citizenship and monthly income, inside the Sort & Filter panel and directly
 * below Sort By (CMS-1905 UC 1).
 *
 * ⚠️ **These two never remove a loan.** Every other group in this panel
 * filters; these only mark which loans the entered details meet the published
 * criteria for. Sitting here invites the opposite assumption, which is why the
 * ticket calls it the most important thing to get right — see
 * `docs/sort-and-filter.md` § 12.
 *
 * Renders only the controls the page's products can actually answer: a market
 * shows the checks its own lender data supports (UC 8), so with neither a
 * citizenship value nor a published minimum income anywhere on the page the
 * whole group is dropped rather than shown empty — the same rule
 * `resolveFilterCategories` applies to a group with no options.
 */
export const YourDetailsGroup: React.FC<YourDetailsGroupProps> = ({
  onIncomeCommit,
  onCitizenshipChange,
}) => {
  const { brand, lang, currencyDisplay } = useCmsClientContext();
  const t = getClientTranslations(ELIGIBILITY_TRANSLATIONS, { brand, lang });
  const bodyId = useId();
  const citizenshipLabelId = useId();
  const incomeId = useId();

  const {
    details,
    citizenshipOptions,
    incomeSupported,
    setCitizenship,
    setMonthlyIncome,
  } = useProductEligibilityState();

  // Expanded on load (AC 1b). State rather than a constant for the same reason
  // as `FilterGroup`: the reader's own choice stands for the visit.
  const [open, setOpen] = useState(true);

  /**
   * The typed text, so the field holds what the reader is part-way through
   * writing. The URL is written on a debounce instead — keying it to every
   * keystroke would re-judge the whole catalogue per character and leave a
   * half-typed "5" standing as an income of five.
   */
  const [draft, setDraft] = useState<string>('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * The committed value wins whenever it did not come from this field: Clear
   * All empties it (AC 1f), and a `cms_income` deep link arrives only after
   * hydration — the server cannot read the URL without dropping the static
   * cache, so the field is necessarily empty on the first render and has to
   * catch up. Seeding the initial state from `details` cannot work for the
   * same reason.
   *
   * Compared on the parsed number rather than the string, or the debounce
   * would fight the reader: committing "5,000" would rewrite the field to
   * "5000" under the cursor.
   */
  const committed = details.monthlyIncome;
  useEffect(() => {
    setDraft((current) =>
      (parseMeasureNumber(current) ?? undefined) === committed
        ? current
        : committed === undefined
          ? ''
          : currencyFormatterNoDecimal.format(committed),
    );
  }, [committed]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const commitIncome = (raw: string) => {
    // The same parser the URL read uses, so what the field accepts and what a
    // link carries cannot diverge.
    const parsed = parseMeasureNumber(raw);
    const value = parsed != null && parsed > 0 ? parsed : undefined;
    // Reported only when the figure actually moved: a blur commits whatever is
    // in the field, so focusing and leaving it would otherwise report the same
    // income again and inflate the count. Same rule CMS-1915 applies to the
    // sort and to Clear All — one event per committed change.
    const changed = value !== committed;
    setMonthlyIncome(value);
    if (changed) onIncomeCommit?.(value);
  };

  const handleIncomeChange = (raw: string) => {
    const formatted = formatIncome(raw);
    setDraft(formatted);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commitIncome(formatted), COMMIT_DELAY_MS);
  };

  const handleIncomeBlur = () => {
    clearTimeout(timer.current);
    commitIncome(draft);
  };

  const handleCitizenship = (value: string) => {
    setCitizenship(value);
    onCitizenshipChange?.(value);
  };

  const showCitizenship = citizenshipOptions.length > 0;
  if (!showCitizenship && !incomeSupported) return null;

  return (
    <div className="flex flex-col gap-spacing/gap/y-xs p-4">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="mhg-alpha-typography-caption flex w-full items-center justify-between text-left font-bold uppercase tracking-wider text-text/secondary text-xs md:text-sm"
      >
        {t.eligibility_group_title}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          className={classNames('size-4 shrink-0 transition-transform', {
            'rotate-180': open,
          })}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* The class carries the state, not the `hidden` attribute — see the same
          note in `FilterGroup`. */}
      <div
        id={bodyId}
        hidden={!open}
        className={classNames(open ? 'flex' : 'hidden', 'flex-col gap-3')}
      >
        {showCitizenship && (
          <div className="flex flex-col gap-1">
            {/* A `<p>`, not a `<label>`: the control below is a listbox
                button rather than a form field, and it takes its accessible
                name from this id. Same shape as the Sort By heading. */}
            <p
              id={citizenshipLabelId}
              className="mhg-alpha-typography-body-2 text-xs text-text/secondary"
            >
              {t.eligibility_citizenship_label}
            </p>
            {/* The panel's own dropdown rather than the car-insurance `Select`:
                same styling, no material-tailwind, and it already handles the
                server/client label mismatch these URL-derived values create. */}
            <SortByDropdown
              options={citizenshipOptions.map((value) => ({
                value,
                label: value,
              }))}
              value={details.citizenship}
              labelId={citizenshipLabelId}
              placeholder={t.eligibility_citizenship_placeholder}
              onChange={handleCitizenship}
            />
          </div>
        )}

        {incomeSupported && (
          <div className="flex flex-col gap-1">
            <label
              htmlFor={incomeId}
              className="mhg-alpha-typography-body-2 text-xs text-text/secondary"
            >
              {t.eligibility_income_label}
            </label>
            <div className="flex items-center gap-1 rounded-radius/button border border-borders/form-default bg-fill/card px-3 py-2 focus-within:border-borders/form-hover">
              <span className="mhg-alpha-typography-body-2 shrink-0 text-sm text-text/secondary">
                {currencyDisplay}
              </span>
              <input
                id={incomeId}
                type="text"
                // Not `type="number"`: it rejects the grouped figures readers
                // paste in and shows spinners a salary has no use for.
                inputMode="numeric"
                value={draft}
                onChange={(event) => handleIncomeChange(event.target.value)}
                onBlur={handleIncomeBlur}
                className="mhg-alpha-typography-body-2 w-full min-w-0 border-0 bg-transparent p-0 text-sm text-text/primary outline-none"
              />
            </div>
          </div>
        )}

        <p className="mhg-alpha-typography-caption text-xs text-text/secondary-lighter">
          {t.eligibility_disclaimer}
        </p>
      </div>
    </div>
  );
};
